/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { CluMigrationJobState, CluMigrationMetadata, LuisAppMetadata } from 'src/clu/core/services/migration/cluMigrationSwaggers';
import { createOperationCall, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';

export const getCluMigrationService = createService(({ context, getEnvironment }) => {
    const { apiUrl: authoringResourceUrl } = getEnvironment({
        apiVersion: 'v3.0-preview'
    });

    const apiOptions = (serviceKey: ServiceKey) =>
        luisRequestOptionsBuilder()
            .useDefaultOptions(serviceKey, true)
            .useCustomHeader('Migrate-From-Luis', 'true')
            .build();

    const getUrl = (baseUrl: string) =>
        urlBuilder()
            .baseUrl(baseUrl)
            .resource('language')
            .resource('analyze-conversations')
            .resource('internal')
            .queryParam('api-version', LuisConstants.Clu.InternalApiVersion);

    const initiateMigration = async ({
        luisRegion,
        luisAppsMetadata,
        languageResourceKey,
        languageResourceEndpoint
    }: {
        luisRegion: string;
        luisAppsMetadata: LuisAppMetadata[];
        languageResourceKey: ServiceKey;
        languageResourceEndpoint: string;
    }) => {
        const url = getUrl(languageResourceEndpoint)
            .resource(':migrateFromLuis')
            .build();

        const cluMigrationMetadata: CluMigrationMetadata = {
            authoringResourceUrl,
            appsMetadata: luisAppsMetadata,
            luisKey: context.serviceKey.value,
            ...(FeatureToggles.EnableSendingLuisRegionInMigration ? { luisRegion } : {})
        };

        const { headers: migrationJobHeaders } = await httpService.post<void>(url, cluMigrationMetadata, apiOptions(languageResourceKey));

        return (<Record<string, string>>migrationJobHeaders)[LuisConstants.OperationHeaders.LocationHeader];
    };

    const pollMigration = async ({
        statusUrl,
        jobId,
        languageResourceKey,
        languageResourceEndpoint,
        operationHandler
    }: {
        statusUrl?: string;
        jobId: string;
        languageResourceKey: ServiceKey;
        languageResourceEndpoint: string;
        operationHandler: OperationHandler<CluMigrationJobState>;
    }): Promise<void> => {
        if (!statusUrl) {
            statusUrl = getUrl(languageResourceEndpoint)
                .resource('migrateFromLuis')
                .resource('jobs', jobId)
                .build();
        }

        await createOperationCall(statusUrl, apiOptions(languageResourceKey), operationHandler, undefined, undefined, undefined, true);
    };

    return {
        initiateMigration,
        pollMigration
    };
});
